import authService from './authService';
import { ensureOk, readData, readList, toQuery } from './apiResponse';
import type { Board, BoardColumn, BoardColumnFormData, BoardFormData, BoardType } from '../types/board';
import type { Activity, BoardActivityFilter } from '../types/activity';

class BoardService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  /**
   * GET /boards?include_archived=&board_type=
   */
  async getAll(params: { includeArchived?: boolean; boardType?: BoardType } = {}): Promise<Board[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/boards${toQuery({ include_archived: params.includeArchived, board_type: params.boardType })}`,
    );
    return readList<Board>(response, 'Falha ao buscar os quadros');
  }

  /**
   * Quadro com colunas
   * GET /boards/:id
   */
  async getById(id: number): Promise<Board> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/boards/${id}`);
    const board = await readData<Board>(response, 'Falha ao buscar o quadro');
    return {
      ...board,
      columns: [...(board.columns ?? [])].sort((a, b) => a.sort_order - b.sort_order),
    };
  }

  /**
   * Abertas + concluídas nos últimos 30 dias, ordenadas por coluna/sort_order
   * GET /activities/board/:boardId
   */
  async getActivities(id: number, filter: BoardActivityFilter = {}): Promise<Activity[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/activities/board/${id}${toQuery({ priority: filter.priority, search: filter.search })}`,
    );
    return readList<Activity>(response, 'Falha ao buscar as atividades do quadro');
  }

  /** POST /boards */
  async create(formData: BoardFormData): Promise<Board> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/boards', formData);
    return readData<Board>(response, 'Falha ao criar o quadro');
  }

  /** PUT /boards/:id */
  async update(id: number, formData: BoardFormData): Promise<Board> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/boards/${id}`, formData);
    return readData<Board>(response, 'Falha ao atualizar o quadro');
  }

  /** DELETE /boards/:id */
  async delete(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/boards/${id}`);
    await ensureOk(response, 'Falha ao excluir o quadro');
  }

  /** POST /boards/:id/columns */
  async createColumn(boardId: number, formData: BoardColumnFormData): Promise<BoardColumn> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/boards/${boardId}/columns`, formData);
    return readData<BoardColumn>(response, 'Falha ao criar a coluna');
  }

  /** PUT /boards/:id/columns/:columnId */
  async updateColumn(boardId: number, columnId: number, formData: BoardColumnFormData): Promise<BoardColumn> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/boards/${boardId}/columns/${columnId}`, formData);
    return readData<BoardColumn>(response, 'Falha ao atualizar a coluna');
  }

  /** PUT /boards/:id/columns/reorder */
  async reorderColumns(boardId: number, columnIds: number[]): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/boards/${boardId}/columns/reorder`, { column_ids: columnIds });
    await ensureOk(response, 'Falha ao reordenar as colunas');
  }

  /** DELETE /boards/:id/columns/:columnId?move_to= */
  async deleteColumn(boardId: number, columnId: number, moveTo?: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/boards/${boardId}/columns/${columnId}${toQuery({ move_to: moveTo })}`);
    await ensureOk(response, 'Falha ao excluir a coluna');
  }
}

export const boardService = new BoardService();
export default boardService;
